import React, { useCallback, useEffect, useRef, useState } from "react"
import anime from "animejs"
import classNames from "classnames"
import { clamp, throttle } from "lodash-es"
import { AnimatePresence, motion } from "motion/react"

import { useResize, useResizeEffect } from "@/hooks/useResize"
import { useTranslation } from "@/locales"
import { isMobile } from "@/utils/misc"
import { BASIC_OPACITY_ANIMATION } from "../../components/Animation"
import { useTopicI18nStore } from "../../locales"
import RELIC_POS_DICT from "./relic.json"

import shelfPng from "../../assets/img/relic/shelf.png"
import shelfWebp from "../../assets/img/relic/shelf.webp"
import styles from "./shelf.module.less"

const RELIC_ORDER = Object.keys(RELIC_POS_DICT)

const OFFSET_X = -30.875

const FOCUS_CENTER = {
    x: -13.875 + 75.375,
    y: 22.6875 + 22.625,
}

const SHOW_BUTTON_OFFSET = 10

const PATH_DICT = Object.entries(RELIC_POS_DICT).reduce(
    (acc, [key, value]) => {
        const points = value.area.points
        let left = points[0].x
        let top = points[0].y
        let right = points[0].x
        let bottom = points[0].y
        let leftMostTop = points[0]
        let rightMostTop = points[0]
        points.forEach((point) => {
            if (point.x < left) {
                left = point.x
            }
            if (point.y < top) {
                top = point.y
            }
            if (point.x > right) {
                right = point.x
            }
            if (point.y > bottom) {
                bottom = point.y
            }
            if (
                point.x < leftMostTop.x ||
                (point.x === leftMostTop.x && point.y < leftMostTop.y)
            ) {
                leftMostTop = point
            }
            if (
                point.x > rightMostTop.x ||
                (point.x === rightMostTop.x && point.y < rightMostTop.y)
            ) {
                rightMostTop = point
            }
        })
        let pathGlobal = ``
        let pathSelf = ``
        let pathInner = ``
        const pathPoint: { cx: number; cy: number; cr: number }[] = []
        const INNER_OFFSET = 22
        const CORNER_OFFSET = 16
        points.forEach((point, index) => {
            // 计算corner
            const lastPoint =
                points[index === 0 ? points.length - 1 : index - 1]
            const nextPoint = points[(index + 1) % points.length]
            const corner = {
                x: 0,
                y: 0,
            }
            if (lastPoint.x === point.x) {
                if (lastPoint.y < point.y) {
                    corner.x = point.x - INNER_OFFSET
                } else {
                    corner.x = point.x + INNER_OFFSET
                }
                if (nextPoint.x < point.x) {
                    corner.y = point.y - INNER_OFFSET
                } else {
                    corner.y = point.y + INNER_OFFSET
                }
            } else {
                if (lastPoint.x < point.x) {
                    corner.y = point.y + INNER_OFFSET
                } else {
                    corner.y = point.y - INNER_OFFSET
                }
                if (nextPoint.y < point.y) {
                    corner.x = point.x + INNER_OFFSET
                } else {
                    corner.x = point.x - INNER_OFFSET
                }
            }
            if (leftMostTop.x === point.x && leftMostTop.y === point.y) {
                pathPoint.push({
                    cx: corner.x - left + 3,
                    cy: corner.y - top + 3,
                    cr: 4,
                })
            } else if (
                rightMostTop.x === point.x &&
                rightMostTop.y === point.y
            ) {
                pathPoint.push({
                    cx: corner.x - left - 3,
                    cy: corner.y - top + 3,
                    cr: 4,
                })
            }
            if (index === 0) {
                pathGlobal += `M ${point.x} ${point.y}`
                pathSelf += `M ${point.x - left} ${point.y - top}`
                if (leftMostTop.x === point.x && leftMostTop.y === point.y) {
                    pathInner += `M ${corner.x - left} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left + CORNER_OFFSET} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left + CORNER_OFFSET} ${corner.y - top}`
                } else if (
                    rightMostTop.x === point.x &&
                    rightMostTop.y === point.y
                ) {
                    pathInner += `M ${corner.x - left - CORNER_OFFSET} ${corner.y - top} L ${corner.x - left - CORNER_OFFSET} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left} ${corner.y - top + CORNER_OFFSET}`
                } else {
                    pathInner += `M ${corner.x - left} ${corner.y - top}`
                }
            } else {
                pathGlobal += ` L ${point.x} ${point.y}`
                pathSelf += ` L ${point.x - left} ${point.y - top}`
                if (leftMostTop.x === point.x && leftMostTop.y === point.y) {
                    pathInner += `L ${corner.x - left} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left + CORNER_OFFSET} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left + CORNER_OFFSET} ${corner.y - top}`
                } else if (
                    rightMostTop.x === point.x &&
                    rightMostTop.y === point.y
                ) {
                    pathInner += `L ${corner.x - left - CORNER_OFFSET} ${corner.y - top} L ${corner.x - left - CORNER_OFFSET} ${corner.y - top + CORNER_OFFSET} L ${corner.x - left} ${corner.y - top + CORNER_OFFSET}`
                } else {
                    pathInner += `L ${corner.x - left} ${corner.y - top}`
                }
            }
        })
        pathGlobal += " Z"
        pathSelf += " Z"
        pathInner += " Z"
        acc[key] = {
            left,
            top,
            width: right - left,
            height: bottom - top,
            pathGlobal,
            pathSelf,
            pathInner,
            pathPoint,
        }
        return acc
    },
    {} as Record<
        string,
        {
            left: number
            top: number
            width: number
            height: number
            pathGlobal: string
            pathSelf: string
            pathInner: string
            pathPoint: { cx: number; cy: number; cr: number }[]
        }
    >
)

const PATH2D_DICT = Object.entries(PATH_DICT).reduce(
    (acc, [key, value]) => {
        acc[key] = new Path2D(value.pathGlobal)
        return acc
    },
    {} as Record<string, Path2D>
)

function GET_FOCUS_TRANS({ x, y }: { x: number; y: number }) {
    return {
        translateX: `${(FOCUS_CENTER.x - OFFSET_X - x / 16).toFixed(3)}rem`,
        translateY: `${(FOCUS_CENTER.y - y / 16).toFixed(3)}rem`,
    }
}

function getFontSize() {
    return Number(
        document.documentElement.style.fontSize?.replace("px", "") ?? "16"
    )
}

function getRelicSrc(key: string) {
    return `${__ASSETS_HOST}assets/img/static/rogue_5/relic/${key}.png`
}

const getCurrentRelic = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number
) => {
    return Object.entries(PATH_DICT).find(([key, value]) => {
        if (x < value.left || x > value.left + value.width) {
            return false
        }
        if (y < value.top || y > value.top + value.height) {
            return false
        }
        return ctx.isPointInPath(PATH2D_DICT[key], x, y)
    })?.[0]
}
const RelicLayer = ({
    currentRelic,
    onChangeRelic,
    lock,
}: {
    currentRelic: string
    onChangeRelic: (key: string, detail?: boolean) => void
    lock: boolean
}) => {
    // shelf effect layer
    const [hoverRelic, setHoverRelic] = useState<string | null>(null)
    const effectLayerRef = useRef<HTMLCanvasElement>(null)
    const lockRef = useRef(lock)
    useEffect(() => {
        lockRef.current = lock
    }, [lock])
    useResizeEffect(
        () => {
            // TODO onResize
            const fontSize = getFontSize()
            const ele = effectLayerRef.current
            if (!ele) {
                return
            }
            const ctx = ele.getContext("2d")
            if (!ctx) {
                return
            }
            const getRealPosition = (x: number, y: number) => {
                const rotated = window.innerWidth < window.innerHeight
                const {
                    left: orig_x,
                    top: orig_y,
                    right: orig_x_bottom,
                } = ele.getBoundingClientRect()
                return rotated
                    ? {
                          x: ((y - orig_y) / fontSize) * 16 * 2,
                          y: ((orig_x_bottom - x) / fontSize) * 16 * 2,
                      }
                    : {
                          x: ((x - orig_x) / fontSize) * 16 * 2,
                          y: ((y - orig_y) / fontSize) * 16 * 2,
                      }
            }
            // TODO 优化为每个元素自己监听
            const moveHandler = throttle((event: MouseEvent) => {
                if (lockRef.current) return
                const { x: xReal, y: yReal } = getRealPosition(
                    event.clientX,
                    event.clientY
                )
                const currentRelic = getCurrentRelic(ctx, xReal, yReal)
                // console.log("move", xReal, yReal, currentRelic)
                setHoverRelic(currentRelic ?? null)
            }, 100)
            const clickHandler = (event: MouseEvent) => {
                if (lockRef.current) return
                const { x: xReal, y: yReal } = getRealPosition(
                    event.clientX,
                    event.clientY
                )
                const currentRelic = getCurrentRelic(ctx, xReal, yReal)
                // console.log("click", xReal, yReal, currentRelic)
                if (currentRelic) {
                    onChangeRelic(currentRelic)
                    setHoverRelic(null)
                }
            }
            const leaveHandler = () => {
                if (lockRef.current) return
                // setHoverRelic(null)
                setTimeout(() => {
                    setHoverRelic(null)
                }, 100)
            }
            const mobile = isMobile()
            if (!mobile) {
                ele.addEventListener("mousemove", moveHandler)
            }
            ele.addEventListener("click", clickHandler)
            if (!mobile) {
                ele.addEventListener("mouseleave", leaveHandler)
            }
            return () => {
                if (!mobile) {
                    ele.removeEventListener("mousemove", moveHandler)
                }
                ele.removeEventListener("click", clickHandler)
                if (!mobile) {
                    ele.removeEventListener("mouseleave", leaveHandler)
                }
            }
        },
        [onChangeRelic],
        200
    )
    return (
        <>
            <svg>
                <defs>
                    <linearGradient
                        id="relic-gradient"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#ff0b50" />
                        <stop offset="20%" stopColor="#ff0b50" />
                        <stop offset="80%" stopColor="white" />
                        <stop offset="100%" stopColor="white" />
                    </linearGradient>
                    <linearGradient
                        id="relic-gradient-inner"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#a30d38" />
                        <stop offset="20%" stopColor="#a30d38" />
                        <stop offset="80%" stopColor="transparent" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                    <linearGradient
                        id="relic-gradient-bg"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#fa0064" />
                        <stop offset="80%" stopColor="transparent" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                </defs>
            </svg>
            {Object.entries(RELIC_POS_DICT).map(([key, value]) => (
                <React.Fragment key={key}>
                    <svg
                        className={styles.relicPath}
                        viewBox={`0 0 ${PATH_DICT[key].width} ${PATH_DICT[key].height}`}
                        style={{
                            left: `${PATH_DICT[key].left / 16}rem`,
                            top: `${PATH_DICT[key].top / 16}rem`,
                            width: `${PATH_DICT[key].width / 16}rem`,
                            height: `${PATH_DICT[key].height / 16}rem`,
                            opacity:
                                currentRelic === key
                                    ? 1
                                    : hoverRelic === key
                                      ? 0.5
                                      : 0,
                        }}
                    >
                        <defs>
                            <path
                                id={`${key}-path`}
                                d={PATH_DICT[key].pathSelf}
                                // style={{ fill: "red", fillOpacity: 0.8 }}
                            />
                            <path
                                id={`${key}-path-inner`}
                                d={PATH_DICT[key].pathInner}
                                // style={{ fill: "red", fillOpacity: 0.8 }}
                            />
                            <clipPath id={`${key}-clip`}>
                                <use xlinkHref={`#${key}-path`} />
                            </clipPath>
                            <clipPath id={`${key}-clip-bg`}>
                                <use xlinkHref={`#${key}-path-inner`} />
                            </clipPath>
                        </defs>
                        <g>
                            <use
                                xlinkHref={`#${key}-path`}
                                clipPath={`url(#${key}-clip)`}
                                strokeWidth="20"
                                stroke="url(#relic-gradient)"
                                fill="none"
                            />
                            <g>
                                <rect
                                    x={0}
                                    y={0}
                                    width={PATH_DICT[key].width}
                                    height={PATH_DICT[key].height}
                                    clipPath={`url(#${key}-clip-bg)`}
                                    fill="url(#relic-gradient-bg)"
                                    fillOpacity={0.35}
                                />
                                <use
                                    xlinkHref={`#${key}-path-inner`}
                                    strokeWidth="5"
                                    stroke="url(#relic-gradient-inner)"
                                    fill="none"
                                />
                                {PATH_DICT[key].pathPoint.map(
                                    (point, index) => (
                                        <circle
                                            key={index}
                                            cx={point.cx}
                                            cy={point.cy}
                                            r={point.cr}
                                            fill="#a30d38"
                                        />
                                    )
                                )}
                            </g>
                        </g>
                    </svg>
                    <img
                        key={key}
                        className={styles.relic}
                        src={getRelicSrc(key)}
                        style={{
                            width: `${value.img.width / 16}rem`,
                            height: `${value.img.height / 16}rem`,
                            left: `${value.img.x / 16}rem`,
                            top: `${value.img.y / 16}rem`,
                        }}
                    />
                </React.Fragment>
            ))}
            <canvas
                className={styles.effectLayer}
                ref={effectLayerRef}
            ></canvas>
        </>
    )
}

const SpecialRelicShelf: React.FC<{
    className?: string
    style?: React.CSSProperties
    setShowButton: (show: boolean) => void
}> = ({ className, style, setShowButton }) => {
    const { t } = useTranslation()
    const {
        objects: { relicDict: RELIC_DICT },
    } = useTopicI18nStore()
    const [detailActive, setDetailActive] = useState(false)
    // 滚动 / 拖动相关逻辑
    // all in rem
    const scrollX = useRef(0)
    const [isDragging, setIsDragging] = useState(false)
    // const [startX, setStartX] = useState(0)
    const startX = useRef(0)
    // const [startShelfX, setStartShelfX] = useState(0)
    const startShelfX = useRef(0)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const realShelfRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // 初始化时显示按钮
        setShowButton(true)
    }, [])

    const [realFontSize, setRealFontSize] = useState(0)

    const updateScrollPosition = useCallback(() => {
        if (!detailActive && realShelfRef.current) {
            if (scrollX.current < -SHOW_BUTTON_OFFSET) {
                setShowButton(false)
            } else {
                setShowButton(true)
            }
            realShelfRef.current.style.transform = `scale(0.5) translateX(${scrollX.current}rem)`
        }
    }, [detailActive, setShowButton])

    useResize(() => {
        setRealFontSize(getFontSize())
        updateScrollPosition()
    }, [])

    const handleStart = useCallback(
        (pageX: number, pageY: number) => {
            if (detailActive) return
            setIsDragging(true)
            if (window.innerWidth >= window.innerHeight)
                startX.current =
                    (pageX - scrollContainerRef.current!.offsetLeft) /
                    realFontSize
            else
                startX.current =
                    (pageY - scrollContainerRef.current!.offsetTop) /
                    realFontSize
            startShelfX.current = scrollX.current
        },
        [detailActive, realFontSize]
    )

    const handleMove = useCallback(
        (pageX: number, pageY: number) => {
            if (detailActive) return
            if (!isDragging) return
            const move =
                window.innerWidth >= window.innerHeight
                    ? pageX - scrollContainerRef.current!.offsetLeft
                    : pageY - scrollContainerRef.current!.offsetTop
            const walk = (move / realFontSize - startX.current) * 2 // 滚动速度系数
            scrollX.current = clamp(
                startShelfX.current + walk,
                (-1053 / 16) * 2,
                0
            )
            updateScrollPosition()
        },
        [detailActive, isDragging, realFontSize, updateScrollPosition]
    )

    const handleEnd = useCallback(() => {
        setIsDragging(false)
    }, [])

    const handleMouseDown = (e: React.MouseEvent) => {
        handleStart(e.pageX, e.pageY)
    }

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            handleMove(e.pageX, e.pageY)
        },
        [handleMove]
    )

    const handleWheel = useCallback(
        (e: React.WheelEvent) => {
            if (isMobile()) return
            if (detailActive) return
            // e.preventDefault()
            if (scrollContainerRef.current) {
                scrollX.current = clamp(
                    scrollX.current - e.deltaY / realFontSize,
                    (-1053 / 16) * 2,
                    0
                )
                updateScrollPosition()
            }
        },
        [detailActive, realFontSize, updateScrollPosition]
    )

    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            if (!isMobile()) return
            handleStart(e.touches[0].pageX, e.touches[0].pageY)
        },
        [handleStart]
    )

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!isMobile()) return
            handleMove(e.touches[0].pageX, e.touches[0].pageY)
        },
        [handleMove]
    )

    // const positionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })

    const [currentRelic, setCurrentRelic] = useState<string>("")

    const animeInstance = useRef<anime.AnimeInstance | null>(null)
    const activateDetail = useCallback(
        (key: string, detail: boolean = false) => {
            setShowButton(false)
            setCurrentRelic(key)
            const { x: left, y: top, width, height } = RELIC_POS_DICT[key].img
            const x = left + width / 2
            const y = top + height / 2
            setDetailActive(true)
            if (animeInstance.current) {
                animeInstance.current.pause()
            }
            animeInstance.current = anime({
                targets: realShelfRef.current,
                ...GET_FOCUS_TRANS({ x, y }),
                scale: 1,
                duration: detail ? 500 : 800,
                easing: "easeInOutSine",
            })
        },
        [setShowButton]
    )
    const deactivateDetail = useCallback(() => {
        setCurrentRelic("")
        setDetailActive(false)
        if (animeInstance.current) {
            animeInstance.current.pause()
        }
        animeInstance.current = anime({
            targets: realShelfRef.current,
            translateX: `${scrollX.current}rem`,
            translateY: `0`,
            scale: 0.5,
            duration: 800,
            easing: "easeInOutSine",
        })
        if (scrollX.current < -SHOW_BUTTON_OFFSET) {
            setShowButton(false)
        } else {
            setShowButton(true)
        }
    }, [setShowButton])
    // const focus = useCallback((x: number, y: number) => {}, [])

    const onNext = useCallback(() => {
        const currentIndex = RELIC_ORDER.indexOf(currentRelic)
        const nextIndex = (currentIndex + 1) % RELIC_ORDER.length
        activateDetail(RELIC_ORDER[nextIndex], true)
    }, [activateDetail, currentRelic])

    const onPrev = useCallback(() => {
        const currentIndex = RELIC_ORDER.indexOf(currentRelic)
        const prevIndex =
            (currentIndex - 1 + RELIC_ORDER.length) % RELIC_ORDER.length
        activateDetail(RELIC_ORDER[prevIndex], true)
    }, [activateDetail, currentRelic])

    return (
        <motion.div
            className={classNames(
                styles.shelfContainer,
                detailActive && styles.activeMode,
                className
            )}
            style={style}
            {...BASIC_OPACITY_ANIMATION({ duration: 0.5, delay: 0.1 })}
        >
            <div className={styles.specialRelicShelf}>
                <div
                    ref={scrollContainerRef}
                    className={styles.scrollContainer}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleEnd}
                    onTouchCancel={handleEnd}
                >
                    <div className={styles.shelfContainer}>
                        <div
                            ref={realShelfRef}
                            className={styles.realShelf}
                            style={{ transform: "scale(0.5)" }}
                        >
                            <picture>
                                <source srcSet={shelfWebp} type="image/webp" />
                                <img src={shelfPng} alt="bg" />
                            </picture>
                            <RelicLayer
                                lock={detailActive}
                                currentRelic={currentRelic}
                                onChangeRelic={activateDetail}
                            />
                        </div>
                    </div>
                </div>
                {/* <div className={styles.maskContainer}></div> */}
            </div>
            <div className={styles.cloudLayer}>
                <div className={styles.cloud}></div>
            </div>

            <AnimatePresence>
                {currentRelic && (
                    <motion.div
                        className={styles.detailUiContainer}
                        {...BASIC_OPACITY_ANIMATION({ duration: 0.5 })}
                    >
                        <div className={styles.switcher}>
                            <div className={styles.left} onClick={onPrev}></div>
                            <div
                                className={styles.right}
                                onClick={onNext}
                            ></div>
                        </div>
                        <div className={styles.contentContainer}>
                            <div className={styles.deco}>
                                {t("subPage.relic")
                                    .split("")
                                    .map((char, index) => (
                                        <span
                                            className={styles.decoChar}
                                            key={index}
                                        >
                                            {char}
                                        </span>
                                    ))}
                            </div>
                            <div
                                className={classNames(
                                    styles.title,
                                    RELIC_DICT[currentRelic].name.length > 20
                                        ? styles.small
                                        : ""
                                )}
                            >
                                {RELIC_DICT[currentRelic].name}
                            </div>
                            <div className={styles.effect}>
                                {RELIC_DICT[currentRelic].usage}
                            </div>
                            <div className={styles.desc}>
                                {RELIC_DICT[currentRelic].desc}
                            </div>
                            <div
                                className={styles.back}
                                onClick={deactivateDetail}
                            >
                                {t("relic.back")}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default SpecialRelicShelf
